import { Component } from '@theme/component';

/**
 * Keeps the mobile pagination dots in step with the reasons scroller.
 *
 * The scroller is native scroll-snap — there is no JS carousel to drive. This only
 * mirrors which slide is in view onto the dots, so they report position rather than
 * decorate. Above the mobile breakpoint the dots are hidden and the observer simply
 * never has anything to report.
 *
 * With JS unavailable the first dot stays marked active, which is the state the
 * scroller starts in anyway.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} scroller - The horizontal scroll container.
 * @property {HTMLElement[]} [items] - The reason cells.
 * @property {HTMLElement[]} [dots] - The pagination dots.
 *
 * @extends Component<Refs>
 */
class SillReasonsToBuyComponent extends Component {
  requiredRefs = ['scroller'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    const { scroller, items, dots } = this.refs;
    if (!items?.length || !dots?.length) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          const index = items.indexOf(/** @type {HTMLElement} */ (entry.target));
          if (index === -1) continue;

          for (const [dotIndex, dot] of dots.entries()) {
            dot.classList.toggle('is-active', dotIndex === index);
          }
        }
      },
      // Only the slide sitting under the middle of the scroller counts as current,
      // which matches where scroll-snap parks it.
      { root: scroller, threshold: 0.6 }
    );

    for (const item of items) this.#observer.observe(item);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

if (!customElements.get('sill-reasons-to-buy')) {
  customElements.define('sill-reasons-to-buy', SillReasonsToBuyComponent);
}
